/**
 * Helper — Bonus juridiques (Lot 6 Phase L1).
 *
 * Centralise la lecture des bonus issus du rôle support
 * `role-juliette-charron-berthet` (avocate-conseil interne) ou,
 * dans une phase ultérieure (L2), d'un cabinet externe à la carte.
 *
 * Utilisé par :
 *   - data/relationEventTemplates.js (modulation des montants d'amende)
 *   - store/slices/relationEventsSlice.js (visibilité du choix défense)
 *   - utils/legal/* (à venir L2, procédures réelles)
 *
 * 17/06/2026 — Création.
 */

import { getSupportRole } from '../data/supportRoles';

/** Id du rôle interne avocate-conseil. */
export const JULIETTE_ROLE_ID = 'role-juliette-charron-berthet';

/**
 * Le rôle juridique est-il en poste ?
 */
export function hasLegalCounsel(state) {
  return !!((state.hiredSupport || {})[JULIETTE_ROLE_ID]);
}

/**
 * Multiplicateur appliqué aux amendes / pénalités. 1.0 = pas de support.
 * Avec Juliette : 1 - 0.30 = 0.70 (–30 %).
 *
 * @returns {number} multiplicateur ∈ ]0, 1]
 */
export function getLegalFineMultiplier(state) {
  if (!hasLegalCounsel(state)) return 1.0;
  const role = getSupportRole(JULIETTE_ROLE_ID);
  if (!role) return 1.0;
  const bonus = (role.bonuses || []).find(b => b.type === 'legal_amende_discount');
  const discount = bonus ? Number(bonus.value) || 0 : 0.30;
  return Math.max(0, Math.min(1, 1 - discount));
}

/**
 * Multiplicateur appliqué aux délais de procédures juridiques. 1.0 = délai
 * de base. Avec Juliette : 1 - 0.50 = 0.50 (procédures 2× plus rapides).
 */
export function getLegalProcedureSpeedMultiplier(state) {
  if (!hasLegalCounsel(state)) return 1.0;
  const role = getSupportRole(JULIETTE_ROLE_ID);
  if (!role) return 1.0;
  const bonus = (role.bonuses || []).find(b => b.type === 'legal_procedure_speed');
  const discount = bonus ? Number(bonus.value) || 0 : 0.50;
  return Math.max(0.1, Math.min(1, 1 - discount));
}

/**
 * Vrai si l'apprenant peut activer l'option « Défense juridique »
 * sur les événements narratifs (OPA, plainte) — débloqué par Juliette
 * (`unlocks: ['juridique_defense']`).
 *
 * Note : Phase 3 expose le choix systématiquement (apprenant joue
 * sans Juliette). Phase L1 le rend pédagogiquement plus efficace
 * (montants atténués). En L2 on rendra le choix vraiment exclusif.
 */
export function canUseLegalDefense(state) {
  return hasLegalCounsel(state);
}
